import { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X,
  Loader2,
  ArrowUpRight,
  FileText,
  CheckCircle2,
  Download,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: '', email: '', company: '', role: '' };

export const AtlasGate = ({ open, onClose }) => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/atlas/download`, form, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: 'application/pdf' }),
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = 'india-mobility-atlas.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setDone(true);
      toast.success('Atlas download started');
    } catch (err) {
      toast.error(
        err?.response?.status === 422
          ? 'Please check your email and try again'
          : 'Could not generate atlas — please try again',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setForm(initial);
    setDone(false);
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
      onClick={close}
      data-testid="atlas-gate-overlay"
    >
      <div
        className="bg-ink-900 border border-amber/40 max-w-lg w-full p-7 md:p-9 relative shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute top-4 right-4 text-zinc-500 hover:text-amber transition-colors"
          aria-label="Close"
          data-testid="atlas-gate-close"
        >
          <X className="h-5 w-5" />
        </button>

        {done ? (
          <div className="py-2">
            <div className="grid h-12 w-12 place-items-center bg-amber/15 border border-amber mb-5">
              <CheckCircle2 className="h-6 w-6 text-amber" />
            </div>
            <div className="overline text-amber mb-2">Download started</div>
            <h3 className="font-display text-2xl md:text-3xl text-white mb-3">
              Atlas is on its way to your downloads.
            </h3>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">
              We've also kept your details on file — if anything in there
              sparks a conversation, just reply to our follow-up and we'll
              take it from there.
            </p>
            <button
              type="button"
              onClick={close}
              data-testid="atlas-gate-close-cta"
              className="inline-flex items-center gap-2 border border-amber bg-amber px-5 py-2.5 text-xs font-mono uppercase tracking-[0.18em] text-black hover:bg-white hover:border-white transition-colors"
            >
              Got it <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="atlas-gate-form">
            <div className="flex items-center gap-3 mb-5">
              <span className="grid h-10 w-10 place-items-center bg-amber/15 border border-amber">
                <FileText className="h-4.5 w-4.5 text-amber" />
              </span>
              <div>
                <div className="overline text-amber">Free download</div>
                <h3 className="font-display text-xl text-white leading-tight">
                  The India Mobility Atlas
                </h3>
              </div>
            </div>

            <p className="text-zinc-400 text-sm leading-relaxed mb-6">
              6-page PDF · FY26 baseline · headline numbers, segments, EV
              trajectory, the ecosystem around the vehicle, and anonymised
              use-cases. Tell us where to send it.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10 mb-5">
              <Field label="Name" required>
                <input
                  required
                  type="text"
                  value={form.name}
                  onChange={update('name')}
                  placeholder="Your full name"
                  data-testid="atlas-input-name"
                  className={inputCls}
                />
              </Field>
              <Field label="Email" required>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  placeholder="you@company.com"
                  data-testid="atlas-input-email"
                  className={inputCls}
                />
              </Field>
              <Field label="Company">
                <input
                  type="text"
                  value={form.company}
                  onChange={update('company')}
                  placeholder="Organisation"
                  data-testid="atlas-input-company"
                  className={inputCls}
                />
              </Field>
              <Field label="Role">
                <input
                  type="text"
                  value={form.role}
                  onChange={update('role')}
                  placeholder="Your role / title"
                  data-testid="atlas-input-role"
                  className={inputCls}
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="atlas-submit"
              className="w-full group inline-flex items-center justify-center gap-2 border border-amber bg-amber px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-black hover:bg-white hover:border-white transition-colors disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Generating
                </>
              ) : (
                <>
                  Get the Atlas (PDF){' '}
                  <Download className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
                </>
              )}
            </button>

            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 text-center leading-relaxed">
              No spam · we use this only to send the Atlas and occasional follow-ups
            </p>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
};

export const AtlasCTA = ({ label = 'Download the India Mobility Atlas (PDF)', testId = 'atlas-cta' }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testId}
        className="group inline-flex items-center gap-2 border border-amber bg-amber px-6 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-black hover:bg-white hover:border-white transition-colors"
      >
        <FileText className="h-4 w-4" />
        {label}
        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>
      <AtlasGate open={open} onClose={() => setOpen(false)} />
    </>
  );
};

const inputCls =
  'w-full bg-ink-900 text-white placeholder:text-zinc-600 px-4 py-3 border-0 outline-none focus:bg-black focus:ring-1 focus:ring-amber focus:ring-inset font-sans text-sm';

const Field = ({ label, children, required }) => (
  <label className="block bg-ink-900">
    <div className="px-4 pt-4 pb-1 flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
        {label}
      </span>
      {required && (
        <span className="font-mono text-[10px] text-amber">required</span>
      )}
    </div>
    {children}
  </label>
);

export default AtlasGate;
